import posthog from "posthog-js";
import { analyticsAllowed, onConsentChange } from "@veyso/core";
import { analyticsConfig } from "../auth/config.js";
import { dropPropelAuthRefreshFailures } from "./exceptionFilter.js";

/**
 * The event shape, for the filters that inspect events before they are sent.
 *
 * Re-exported so nothing else has to import `posthog-js` — the one rule that
 * keeps every call site behind the consent gate (see tests/site-legal.test.ts).
 */
export type { CaptureResult } from "posthog-js";

/**
 * Analytics, started only once somebody has said it may.
 *
 * Three things here are deliberately not what `@posthog/wizard` generated, and
 * each of them is load-bearing.
 *
 * **It is not configured at build time.** The wizard read the key from
 * `import.meta.env.VITE_POSTHOG_KEY`, which compiles it into the bundle — and
 * the portal ships as one static image that is meant to run in every
 * environment. A key baked in at `vite build` makes the image
 * environment-specific and means a rebuild to point it at a different project.
 * So it comes from `window.VEYSO_CONFIG`, which nginx writes from its own
 * environment, exactly as the auth tenant already does.
 *
 * **A missing key is not an error.** The wizard threw in development when the
 * variable was absent, which would stop the app dead on a laptop that has never
 * had a PostHog project — and `docker compose up` with no keys at all is a
 * supported way to run this. Nothing else in this codebase treats a missing
 * provider as fatal, and analytics is the last thing that should.
 *
 * **It does not start until consent.** The Cookie Policy says PostHog is "not
 * loaded at all until you accept analytics cookies", and that has to be true
 * rather than aspirational. `init` is the call that sets the cookie and starts
 * sending, so it is what waits.
 *
 * Note that the portal is a different origin from the marketing site — today
 * veyso-web.fly.dev and veyso-site.fly.dev — and a consent answer does not
 * cross an origin. Accepting on the site does not accept here; this app asks
 * its own visitor. See `packages/core/src/consent.ts`.
 */
let started = false;

function start(): void {
  if (started) return;

  const config = analyticsConfig();
  if (!config) return;

  posthog.init(config.key, {
    api_host: config.host,
    // Only once the page has said so. Without this, `init` writes its cookie
    // and begins sending immediately, which is the behaviour being gated.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      // Console errors can carry anything a developer chose to log, including
      // fragments of somebody's project. Exceptions are bounded; console output
      // is not.
      capture_console_errors: false,
    },
    before_send: dropPropelAuthRefreshFailures,
  });
  started = true;
}

if (analyticsAllowed()) start();

// So accepting takes effect on the page the visitor is already looking at,
// rather than on their next one.
onConsentChange((value) => {
  if (value === "accepted") start();
  // Declining after accepting: stop sending. The library keeps running because
  // unloading it is not something it supports, but it stops recording and
  // clears what it held.
  if (value === "declined" && started) posthog.opt_out_capturing();
});

/**
 * Every call site goes through this rather than importing the client directly.
 *
 * `posthog.capture` on an uninitialised client queues the event and sends it
 * the moment `init` runs — which would mean everything done before the banner
 * was answered arriving the instant somebody accepted. That is worse than
 * losing it: it is a recording of the period we said we were not recording.
 */
export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!started) return;
  posthog.capture(event, properties);
}

/** Who is signed in, for joining sessions together. Nothing more than the id. */
export function identify(userId: string): void {
  if (!started) return;
  posthog.identify(userId);
}

/** On sign-out, so the next person on this browser is not the last one. */
export function resetIdentity(): void {
  if (!started) return;
  posthog.reset();
}

/** Exported for tests. */
export function analyticsStarted(): boolean {
  return started;
}
